// ==========================================================================
// What the browser still does that an app never does.
//
// The viewport meta tag and `touch-action` in base.css already say "no zoom".
// Neither is enough on its own, and this file is the rest:
//
//   · iOS Safari ignores `user-scalable=no` since iOS 10 and lets a pinch
//     through anyway. Its proprietary `gesture*` events are the one handle it
//     gives to stop that.
//   · A desktop trackpad pinch arrives as `wheel` with `ctrlKey` set, and
//     Ctrl/⌘ with + − 0 zoom the whole window. In a browser tab that is the
//     reader's right and we leave it alone; in the INSTALLED app it is a window
//     that suddenly renders at 125 % with no way to tell why.
//   · A long press on a link or an image opens the browser's own menu ("open in
//     new tab", "download image") — a web page giving itself away. Text fields
//     keep theirs: that is where copy and paste live.
//
// `html.is-standalone` is set here so CSS can treat the installed app
// differently from the same pages in a tab.
// ==========================================================================

const ZOOM_KEYS = new Set(['+', '=', '-', '_', '0'])

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.matchMedia('(display-mode: window-controls-overlay)').matches
    || window.navigator.standalone === true
}

function isTextTarget(target) {
  if (!(target instanceof Element)) return false
  return Boolean(target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]'))
}

export function setupNativeFeel() {
  const root = document.documentElement

  const sync = () => root.classList.toggle('is-standalone', isStandalone())
  sync()
  // An installed app can be opened in a tab and vice versa; the media query is
  // the only thing that knows which one this is right now.
  window.matchMedia('(display-mode: standalone)').addEventListener?.('change', sync)

  // ── Pinch on iOS ────────────────────────────────────────────────────────
  // Always, not only installed: index.html already forbids zoom everywhere,
  // this is iOS being made to listen.
  const stop = event => event.preventDefault()
  for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
    document.addEventListener(type, stop, { passive: false })
  }

  // ── El `:active` de iOS ─────────────────────────────────────────────────
  // Safari de iOS no aplica `:active` al tocar a menos que la página escuche
  // `touchstart` en algún sitio: sin esto, el `.tap` de base.css y el toque de
  // las filas de UiTable no se veían nunca en un iPhone. Pasivo y vacío: no
  // hace nada más que existir.
  document.addEventListener('touchstart', () => {}, { passive: true })

  // ── Browser zoom in the installed app ───────────────────────────────────
  // A component that zooms its own content (the file viewer) handles its wheel
  // before this runs on the window, so it keeps working.
  window.addEventListener('wheel', (event) => {
    if ((event.ctrlKey || event.metaKey) && root.classList.contains('is-standalone')) event.preventDefault()
  }, { passive: false })

  window.addEventListener('keydown', (event) => {
    if (!(event.ctrlKey || event.metaKey) || event.altKey) return
    if (!ZOOM_KEYS.has(event.key)) return
    if (root.classList.contains('is-standalone')) event.preventDefault()
  })

  // ── The browser's long-press menu ───────────────────────────────────────
  // Only for a finger and only in the installed app: a right click on a
  // desktop is a deliberate act, and a tab is still a web page.
  window.addEventListener('contextmenu', (event) => {
    if (!root.classList.contains('is-standalone')) return
    if (!window.matchMedia('(pointer: coarse)').matches) return
    if (isTextTarget(event.target)) return
    // Selected text keeps its menu: copying a reference number is a real need.
    if (String(window.getSelection?.() || '').trim()) return
    event.preventDefault()
  })
}
